import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Archive,
    Calculator,
    ChevronRight,
    Download,
    FileText,
    HeartPulse,
    Loader2,
    RefreshCw,
    Save,
    Users,
} from "lucide-react";
import { toast } from "sonner";
import { aktueryaMerkez, sigortaDosyaListeOzet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, IconTab, INPUT_CLS, KpiCard, SELECT_CLS, tl, WorkflowStep } from "./aktuerya/AktueryaUiParts";
import { KayitlarPanel, OlumluForm, RaporMetaForm, YaralanmaliForm } from "./aktuerya/AktueryaFormPanels";
import AktueryaSonucPanel from "./aktuerya/AktueryaSonucPanel";

const MOD_TABS = [
    { id: "yaralanmali", label: "SİG", icon: HeartPulse },
    { id: "olumlu", label: "DYK", icon: Users },
    { id: "kayitlar", label: "Kayıtlar", icon: Archive },
];

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

export default function AktueryaMerkezPage() {
    const [searchParams] = useSearchParams();
    const initialDosyaId = searchParams.get("dosya_id") || "";

    const [mod, setMod] = useState("yaralanmali");
    const [loading, setLoading] = useState(false);
    const [sabitler, setSabitler] = useState(null);
    const [sonuc, setSonuc] = useState(null);
    const [kayitId, setKayitId] = useState(null);
    const [kayitlar, setKayitlar] = useState([]);
    const [dosyalar, setDosyalar] = useState([]);
    const [kayitArama, setKayitArama] = useState("");

    const [dosyaId, setDosyaId] = useState(initialDosyaId);
    const [baslik, setBaslik] = useState("");

    const [yaralanmali, setYaralanmali] = useState({
        dogum_tarihi: "",
        kaza_tarihi: "",
        rapor_tarihi: todayIso(),
        cinsiyet: "E",
        aylik_net_gelir: "",
        maluliyet_orani: "25",
        kusur_orani: "100",
        sgk_mahsup: "0",
        sigorta_odeme: "0",
        artis_orani: "10",
        iskonto_orani: "10",
        pasif_aylik_gelir: "",
        gecmis_faiz_orani: "24",
        gecmis_donem_dahil: true,
        gig_dahil: false,
        gig_gun_sayisi: "",
        gig_maluliyet_orani: "",
        gig_otomatik_gun: true,
        bakim_gideri_dahil: false,
        bakim_aylik_gider: "",
    });

    const [raporMeta, setRaporMeta] = useState({
        mahkeme: "",
        esas_no: "",
        davaci: "",
        davali: "",
        muvekkil: "",
    });

    const [bilirkişiKanaati, setBilirkişiKanaati] = useState("");

    const [olumlu, setOlumlu] = useState({
        vefat_dogum_tarihi: "",
        vefat_cinsiyet: "E",
        kaza_tarihi: "",
        rapor_tarihi: todayIso(),
        aylik_net_gelir: "",
        kusur_orani: "100",
        sigorta_odeme: "0",
        artis_orani: "10",
        iskonto_orani: "10",
        pasif_aylik_gelir: "",
        hak_sahipleri: [{ ad_soyad: "", yakinlik: "es", cinsiyet: "K", dogum_tarihi: "", pay_adedi: 2 }],
    });

    const loadSabitler = useCallback(async () => {
        try {
            const s = await aktueryaMerkez.sabitler();
            setSabitler(s);
            setYaralanmali((p) => ({
                ...p,
                pasif_aylik_gelir: p.pasif_aylik_gelir || String(s.net_asgari_ucret || ""),
                artis_orani: String((s.default_artis_orani || 0.1) * 100),
                iskonto_orani: String((s.default_iskonto_orani || 0.1) * 100),
            }));
            setOlumlu((p) => ({
                ...p,
                pasif_aylik_gelir: p.pasif_aylik_gelir || String(s.net_asgari_ucret || ""),
                artis_orani: String((s.default_artis_orani || 0.1) * 100),
                iskonto_orani: String((s.default_iskonto_orani || 0.1) * 100),
            }));
        } catch {
            toast.error("Sabitler yüklenemedi");
        }
    }, []);

    const loadKayitlar = useCallback(async () => {
        try {
            const params = dosyaId ? { dosya_id: dosyaId } : {};
            const r = await aktueryaMerkez.liste(params);
            setKayitlar(r.items || []);
        } catch {
            setKayitlar([]);
        }
    }, [dosyaId]);

    useEffect(() => {
        loadSabitler();
        sigortaDosyaListeOzet({ limit: 100 })
            .then((r) => setDosyalar(r.items || r.results || []))
            .catch(() => setDosyalar([]));
    }, [loadSabitler]);

    useEffect(() => {
        if (initialDosyaId) onDosyaSec(initialDosyaId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDosyaId]);

    useEffect(() => {
        if (mod === "kayitlar") loadKayitlar();
    }, [mod, loadKayitlar]);

    const payload = useMemo(() => {
        const pct = (v) => Number(v) / 100;
        const num = (v) => (v === "" || v == null ? undefined : Number(v));
        const baseMeta = {
            rapor_meta: raporMeta,
            bilirkişi_kanaati_ozel: bilirkişiKanaati.trim() || undefined,
        };
        const tur = mod === "olumlu" || sonuc?.hesap_turu === "olumlu" ? "olumlu" : "yaralanmali";
        if (tur === "olumlu") {
            return {
                hesap_turu: "olumlu",
                baslik,
                sigorta_dosyasi_id: dosyaId || undefined,
                ...baseMeta,
                ...olumlu,
                artis_orani: pct(olumlu.artis_orani),
                iskonto_orani: pct(olumlu.iskonto_orani),
                aylik_net_gelir: Number(olumlu.aylik_net_gelir) || 0,
                kusur_orani: Number(olumlu.kusur_orani) || 100,
                sigorta_odeme: Number(olumlu.sigorta_odeme) || 0,
                pasif_aylik_gelir: Number(olumlu.pasif_aylik_gelir) || undefined,
                hak_sahipleri: olumlu.hak_sahipleri.filter((h) => h.ad_soyad?.trim()),
            };
        }
        return {
            hesap_turu: "yaralanmali",
            baslik,
            sigorta_dosyasi_id: dosyaId || undefined,
            ...baseMeta,
            ...yaralanmali,
            artis_orani: pct(yaralanmali.artis_orani),
            iskonto_orani: pct(yaralanmali.iskonto_orani),
            gecmis_faiz_orani: pct(yaralanmali.gecmis_faiz_orani || 24),
            aylik_net_gelir: Number(yaralanmali.aylik_net_gelir) || 0,
            maluliyet_orani: Number(yaralanmali.maluliyet_orani) || 0,
            kusur_orani: Number(yaralanmali.kusur_orani) || 100,
            sgk_mahsup: Number(yaralanmali.sgk_mahsup) || 0,
            sigorta_odeme: Number(yaralanmali.sigorta_odeme) || 0,
            pasif_aylik_gelir: Number(yaralanmali.pasif_aylik_gelir) || undefined,
            gig_dahil: !!yaralanmali.gig_dahil,
            gig_gun_sayisi: num(yaralanmali.gig_gun_sayisi),
            gig_maluliyet_orani: num(yaralanmali.gig_maluliyet_orani),
            gig_otomatik_gun: !!yaralanmali.gig_otomatik_gun,
            bakim_gideri_dahil: !!yaralanmali.bakim_gideri_dahil,
            bakim_aylik_gider: num(yaralanmali.bakim_aylik_gider),
        };
    }, [mod, baslik, dosyaId, yaralanmali, olumlu, sonuc?.hesap_turu, raporMeta, bilirkişiKanaati]);

    const stepDosya = !!dosyaId;
    const stepParam = mod === "yaralanmali" ? !!yaralanmali.dogum_tarihi && !!yaralanmali.kaza_tarihi : !!olumlu.vefat_dogum_tarihi;
    const stepHesap = !!sonuc?.success;
    const stepKayit = !!kayitId;

    async function onDosyaSec(id, { ocr = true } = {}) {
        if (!id) {
            setDosyaId("");
            return;
        }
        setDosyaId(String(id));
        setLoading(true);
        try {
            const pre = await aktueryaMerkez.dosyaOnDoldur(id, { ocr });
            if (pre.kaza_tarihi) {
                setYaralanmali((p) => ({
                    ...p,
                    kaza_tarihi: pre.kaza_tarihi,
                    dogum_tarihi: pre.dogum_tarihi || p.dogum_tarihi,
                    cinsiyet: pre.cinsiyet?.[0]?.toUpperCase() === "K" ? "K" : "E",
                    kusur_orani: String(pre.kusur_orani ?? p.kusur_orani),
                    aylik_net_gelir: pre.aylik_net_gelir != null ? String(pre.aylik_net_gelir) : p.aylik_net_gelir,
                    maluliyet_orani: pre.maluliyet_orani != null ? String(pre.maluliyet_orani) : p.maluliyet_orani,
                    gig_dahil: pre.gig_dahil ?? p.gig_dahil,
                    gig_gun_sayisi: pre.gig_gun_sayisi != null ? String(pre.gig_gun_sayisi) : p.gig_gun_sayisi,
                    bakim_gideri_dahil: pre.bakim_gideri_dahil ?? p.bakim_gideri_dahil,
                    bakim_aylik_gider: pre.bakim_aylik_gider != null ? String(pre.bakim_aylik_gider) : p.bakim_aylik_gider,
                }));
                setOlumlu((p) => ({
                    ...p,
                    kaza_tarihi: pre.kaza_tarihi,
                    vefat_dogum_tarihi: pre.dogum_tarihi || p.vefat_dogum_tarihi,
                    vefat_cinsiyet: pre.cinsiyet?.[0]?.toUpperCase() === "K" ? "K" : "E",
                    kusur_orani: String(pre.kusur_orani ?? p.kusur_orani),
                    aylik_net_gelir: pre.aylik_net_gelir != null ? String(pre.aylik_net_gelir) : p.aylik_net_gelir,
                }));
            }
            if (pre.rapor_meta) {
                setRaporMeta((p) => ({
                    ...p,
                    mahkeme: pre.rapor_meta.mahkeme || p.mahkeme,
                    esas_no: pre.rapor_meta.esas_no || p.esas_no,
                    davaci: pre.rapor_meta.davaci || p.davaci,
                    davali: pre.rapor_meta.davali || p.davali,
                }));
            }
            if (pre.muvekkil_label) {
                setRaporMeta((p) => ({ ...p, muvekkil: pre.muvekkil_label }));
            }
            if (pre.dosya_no) setBaslik(`Dosya ${pre.dosya_no} — Aktüerya`);
            const ocrInfo = pre.ocr?.evrak_sayisi ? ` · OCR: ${pre.ocr.evrak_sayisi} evrak` : "";
            toast.success(`Dosya verileri yüklendi${ocrInfo}`);
        } catch {
            toast.error("Dosya ön doldurma başarısız");
        } finally {
            setLoading(false);
        }
    }

    async function hesapla() {
        setLoading(true);
        setSonuc(null);
        try {
            const p = mod === "olumlu" ? { ...payload, hesap_turu: "olumlu" } : { ...payload, hesap_turu: "yaralanmali" };
            const r = await aktueryaMerkez.hesapla(p);
            if (!r.success) {
                toast.error(r.error || "Hesap başarısız");
                return;
            }
            setSonuc(r);
            toast.success("Aktüerya hesabı tamamlandı");
        } catch (e) {
            toast.error(e?.response?.data?.error || "Hesap hatası");
        } finally {
            setLoading(false);
        }
    }

    async function kaydet() {
        if (!sonuc?.success) {
            toast.error("Önce hesaplama yapın");
            return;
        }
        setLoading(true);
        try {
            const p = mod === "olumlu" ? { ...payload, hesap_turu: "olumlu" } : { ...payload, hesap_turu: "yaralanmali" };
            const r = kayitId ? await aktueryaMerkez.guncelle(kayitId, p) : await aktueryaMerkez.kaydet(p);
            setKayitId(r.id);
            toast.success(kayitId ? "Kayıt güncellendi" : "Kayıt oluşturuldu");
            loadKayitlar();
        } catch (e) {
            toast.error(e?.response?.data?.error || "Kayıt hatası");
        } finally {
            setLoading(false);
        }
    }

    async function kayitSil(id) {
        if (!window.confirm("Bu aktüerya kaydını silmek istediğinize emin misiniz?")) return;
        setLoading(true);
        try {
            await aktueryaMerkez.sil(id);
            if (kayitId === id) {
                setKayitId(null);
                setSonuc(null);
            }
            toast.success("Kayıt silindi");
            loadKayitlar();
        } catch {
            toast.error("Silme başarısız");
        } finally {
            setLoading(false);
        }
    }

    function pctField(v) {
        const n = Number(v);
        return String(n <= 1 && n > 0 ? n * 100 : n || 10);
    }

    function formuDoldur(girdi, detay) {
        if (!girdi) return;
        setBaslik(girdi.baslik || detay?.baslik || "");
        if (girdi.rapor_meta) setRaporMeta((p) => ({ ...p, ...girdi.rapor_meta }));
        setBilirkişiKanaati(girdi.bilirkişi_kanaati_ozel || "");
        if (girdi.sigorta_dosyasi_id || detay?.dosya_id) {
            setDosyaId(String(girdi.sigorta_dosyasi_id || detay.dosya_id));
        }
        if (girdi.hesap_turu === "olumlu" || detay?.hesap_turu === "olumlu") {
            setOlumlu((p) => ({
                ...p,
                vefat_dogum_tarihi: girdi.vefat_dogum_tarihi || p.vefat_dogum_tarihi,
                vefat_cinsiyet: girdi.vefat_cinsiyet || p.vefat_cinsiyet,
                kaza_tarihi: girdi.kaza_tarihi || p.kaza_tarihi,
                rapor_tarihi: girdi.rapor_tarihi || p.rapor_tarihi,
                aylik_net_gelir: String(girdi.aylik_net_gelir ?? p.aylik_net_gelir),
                kusur_orani: String(girdi.kusur_orani ?? p.kusur_orani),
                sigorta_odeme: String(girdi.sigorta_odeme ?? p.sigorta_odeme),
                artis_orani: pctField(girdi.artis_orani),
                iskonto_orani: pctField(girdi.iskonto_orani),
                hak_sahipleri: girdi.hak_sahipleri?.length ? girdi.hak_sahipleri : p.hak_sahipleri,
            }));
            setMod("olumlu");
        } else {
            setYaralanmali((p) => ({
                ...p,
                dogum_tarihi: girdi.dogum_tarihi || p.dogum_tarihi,
                kaza_tarihi: girdi.kaza_tarihi || p.kaza_tarihi,
                rapor_tarihi: girdi.rapor_tarihi || p.rapor_tarihi,
                cinsiyet: girdi.cinsiyet || p.cinsiyet,
                aylik_net_gelir: String(girdi.aylik_net_gelir ?? p.aylik_net_gelir),
                maluliyet_orani: String(girdi.maluliyet_orani ?? p.maluliyet_orani),
                kusur_orani: String(girdi.kusur_orani ?? p.kusur_orani),
                sgk_mahsup: String(girdi.sgk_mahsup ?? p.sgk_mahsup),
                sigorta_odeme: String(girdi.sigorta_odeme ?? p.sigorta_odeme),
                artis_orani: pctField(girdi.artis_orani),
                iskonto_orani: pctField(girdi.iskonto_orani),
                gecmis_faiz_orani: pctField(girdi.gecmis_faiz_orani || 0.24),
                gecmis_donem_dahil: girdi.gecmis_donem_dahil ?? p.gecmis_donem_dahil,
                gig_dahil: girdi.gig_dahil ?? p.gig_dahil,
                gig_gun_sayisi: girdi.gig_gun_sayisi != null ? String(girdi.gig_gun_sayisi) : p.gig_gun_sayisi,
                gig_maluliyet_orani: girdi.gig_maluliyet_orani != null ? String(girdi.gig_maluliyet_orani) : p.gig_maluliyet_orani,
                gig_otomatik_gun: girdi.gig_otomatik_gun ?? p.gig_otomatik_gun,
                bakim_gideri_dahil: girdi.bakim_gideri_dahil ?? p.bakim_gideri_dahil,
                bakim_aylik_gider: girdi.bakim_aylik_gider != null ? String(girdi.bakim_aylik_gider) : p.bakim_aylik_gider,
            }));
            setMod("yaralanmali");
        }
    }

    async function kayitYukle(id) {
        const d = await aktueryaMerkez.detay(id);
        setSonuc(d.sonuc);
        setKayitId(id);
        formuDoldur(d.girdi, d);
        setMod(d.hesap_turu === "olumlu" ? "olumlu" : "yaralanmali");
        toast.success("Kayıt yüklendi");
    }

    function yeniKayit() {
        setKayitId(null);
        setSonuc(null);
        setBaslik("");
        setMod("yaralanmali");
        toast.message("Yeni hesap");
    }

    async function pdfIndir() {
        setLoading(true);
        try {
            const p = kayitId ? { id: kayitId } : payload;
            const blob = await aktueryaMerkez.pdf(p);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `aktuerya-${kayitId || "taslak"}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("PDF indirildi");
        } catch {
            toast.error("PDF oluşturulamadı");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="space-y-5 pb-16">
            {/* Hero */}
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 via-[#121A2F]/90 to-slate-950 p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider mb-2">
                            <Link to="/" className="hover:text-white transition">Panel</Link>
                            <ChevronRight className="h-3 w-3" />
                            <span className="text-slate-400">Operasyon</span>
                            <ChevronRight className="h-3 w-3" />
                            <span className="text-[#D4AF37]">Aktüerya Merkezi</span>
                        </div>
                        <h1 className="font-display text-2xl md:text-3xl text-white flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-black/30">
                                <Calculator className="h-5 w-5 text-[#D4AF37]" />
                            </span>
                            Aktüerya Merkezi
                        </h1>
                        <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
                            TRH-2010 yaşam tablosu · Yargıtay progresif rant · Kesin + tahmini band · Kurumsal bilirkişi PDF
                        </p>
                        {kayitId ? (
                            <div className="inline-flex items-center gap-2 mt-3 text-xs text-slate-400 border border-white/10 rounded-full px-3 py-1">
                                <FileText className="h-3 w-3" /> Kayıt #{kayitId}
                                {sonuc ? <span className="text-emerald-400">· Hesap hazır</span> : null}
                            </div>
                        ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={loadSabitler} disabled={loading} className="border-white/10">
                            <RefreshCw className="h-4 w-4 mr-1" /> Sabitler
                        </Button>
                        <Button size="sm" onClick={hesapla} disabled={loading || mod === "kayitlar"} className="bg-[#D4AF37] text-black hover:bg-[#c4a030] min-w-[100px]">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4 mr-1" />}
                            Hesapla
                        </Button>
                        <Button variant="secondary" size="sm" onClick={kaydet} disabled={loading || !sonuc}>
                            <Save className="h-4 w-4 mr-1" /> {kayitId ? "Güncelle" : "Kaydet"}
                        </Button>
                        <Button variant="secondary" size="sm" onClick={pdfIndir} disabled={loading || !sonuc}>
                            <Download className="h-4 w-4 mr-1" /> PDF
                        </Button>
                    </div>
                </div>

                {/* Workflow */}
                <div className="flex flex-wrap items-center gap-4 mt-6 pt-5 border-t border-white/10">
                    <WorkflowStep n={1} label="Dosya" active={!stepDosya} done={stepDosya} />
                    <ChevronRight className="h-3 w-3 text-slate-700 hidden sm:block" />
                    <WorkflowStep n={2} label="Parametre" active={stepDosya && !stepParam} done={stepParam} />
                    <ChevronRight className="h-3 w-3 text-slate-700 hidden sm:block" />
                    <WorkflowStep n={3} label="Hesap" active={stepParam && !stepHesap} done={stepHesap} />
                    <ChevronRight className="h-3 w-3 text-slate-700 hidden sm:block" />
                    <WorkflowStep n={4} label="Kayıt / PDF" active={stepHesap && !stepKayit} done={stepKayit} />
                </div>
            </div>

            {/* Sabitler KPI */}
            {sabitler ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiCard label="Yaşam Tablosu" value="TRH-2010" hint={`Emeklilik E:${sabitler.emeklilik_erkek} · K:${sabitler.emeklilik_kadin}`} />
                    <KpiCard label="Net Asgari 2026" value={tl(sabitler.net_asgari_ucret)} accent="emerald" />
                    <KpiCard label="ZMSS Bedeni Limit" value={tl(sabitler.zmss_bedeni_limit)} accent="gold" />
                    <KpiCard label="Yöntem" value="Progresif Rant" hint="%10 artış · %10 iskonto · Yargıtay" accent="sky" />
                </div>
            ) : null}

            <div className="grid xl:grid-cols-12 gap-5">
                {/* Sol — Form */}
                <div className="xl:col-span-5 space-y-4">
                    <IconTab tabs={MOD_TABS} active={mod} onChange={setMod} />

                    {mod !== "kayitlar" ? (
                        <div className="rounded-xl border border-white/10 bg-slate-950/30 p-4 space-y-4">
                            <Field label="Sigorta Dosyası">
                                <div className="flex gap-2">
                                    <select className={`${SELECT_CLS} flex-1`} value={dosyaId} onChange={(e) => onDosyaSec(e.target.value)}>
                                        <option value="">— Dosya seçin (opsiyonel) —</option>
                                        {dosyalar.map((d) => (
                                            <option key={d.id} value={d.id}>
                                                {d.dosya_no || d.id} · {d.plaka || d.muvekkil_label || ""}
                                            </option>
                                        ))}
                                    </select>
                                    {dosyaId ? (
                                        <Button type="button" size="sm" variant="outline" className="shrink-0 border-white/10" disabled={loading} onClick={() => onDosyaSec(dosyaId, { ocr: true })}>
                                            OCR
                                        </Button>
                                    ) : null}
                                </div>
                            </Field>
                            <Field label="Hesap Başlığı">
                                <Input className={INPUT_CLS} value={baslik} onChange={(e) => setBaslik(e.target.value)} placeholder="Örn: Dosya 2024/123 — SİG hesabı" />
                            </Field>
                        </div>
                    ) : null}

                    {mod === "yaralanmali" && <YaralanmaliForm state={yaralanmali} setState={setYaralanmali} />}
                    {mod === "olumlu" && <OlumluForm state={olumlu} setState={setOlumlu} />}
                    {mod !== "kayitlar" && <RaporMetaForm state={raporMeta} setState={setRaporMeta} />}
                    {mod === "kayitlar" && (
                        <KayitlarPanel
                            kayitlar={kayitlar}
                            kayitId={kayitId}
                            arama={kayitArama}
                            setArama={setKayitArama}
                            onYeni={yeniKayit}
                            onLoad={kayitYukle}
                            onEdit={kayitYukle}
                            onSil={kayitSil}
                        />
                    )}
                </div>

                {/* Sağ — Sonuç */}
                <div className="xl:col-span-7">
                    <AktueryaSonucPanel sonuc={sonuc} kanaat={bilirkişiKanaati} onKanaatChange={setBilirkişiKanaati} />
                </div>
            </div>
        </div>
    );
}
